import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, Shield, Loader2, CheckCircle2 } from "lucide-react";

export default function CambiarContrasenaPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, forcePasswordChange, clearForcePasswordChange } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!forcePasswordChange && !formData.current_password) {
      toast.error("Debe ingresar su contraseña actual");
      return;
    }
    
    if (formData.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: forcePasswordChange ? null : formData.current_password,
        new_password: formData.new_password
      }, getAuthHeaders());
      
      toast.success("Contraseña actualizada correctamente");
      clearForcePasswordChange();
      
      // Redirect to home page
      setTimeout(() => {
        navigate(user?.home_page || "/");
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-full max-w-md shadow-xl dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#0C314C] rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#0C314C] dark:text-white">
            {forcePasswordChange ? "Cambio de Contraseña Requerido" : "Cambiar Contraseña"}
          </CardTitle>
          <CardDescription className="dark:text-slate-400">
            {forcePasswordChange 
              ? "Por seguridad, debe cambiar su contraseña antes de continuar"
              : "Ingrese su contraseña actual y la nueva contraseña"
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Password - only show if not forced change */}
            {!forcePasswordChange && (
              <div className="space-y-2">
                <Label htmlFor="current_password" className="dark:text-slate-300">
                  Contraseña Actual
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.current_password}
                    onChange={(e) => setFormData({...formData, current_password: e.target.value})}
                    placeholder="••••••••"
                    className="pl-10 pr-10 dark:bg-slate-700 dark:border-slate-600"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
            
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password" className="dark:text-slate-300">
                Nueva Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  value={formData.new_password}
                  onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                  placeholder="••••••••"
                  className="pl-10 pr-10 dark:bg-slate-700 dark:border-slate-600"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Mínimo 6 caracteres
              </p>
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="dark:text-slate-300">
                Confirmar Nueva Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  placeholder="••••••••"
                  className="pl-10 pr-10 dark:bg-slate-700 dark:border-slate-600"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirm_password && formData.new_password === formData.confirm_password && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Las contraseñas coinciden
                </p>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0C314C] hover:bg-[#2a4d7a] text-white mt-6"
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Cambiando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Cambiar Contraseña
                </>
              )}
            </Button>
            
            {!forcePasswordChange && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(-1)}
                className="w-full"
              >
                Cancelar
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
